import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import {connect} from 'react-redux';

import {TIMELINE_START, TIMELINE_END} from 'ducks/allocations';
import {DATE_FORMAT_API} from 'utils/datetime';
import {StatisticsBox, TimelineGroup} from './components';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {fetchAllocationStatistics, fetchAllocations} from 'ducks/allocations';

import Timeline, {TodayMarker} from 'react-calendar-timeline/lib';
import 'react-calendar-timeline/lib/Timeline.css';

import './styles.css';

const TOOLTIP_NAME = 'roomTypeTooltip';

const KEYS_MAPPING = {
  groupIdKey: 'id',
  groupTitleKey: 'title',
  groupRightTitleKey: 'rightTitle',
  itemIdKey: 'id',
  itemTitleKey: 'title',
  itemDivTitleKey: 'title',
  itemGroupKey: 'group',
  itemTimeStartKey: 'startDate',
  itemTimeEndKey: 'endDate'
};

const TODAY_MARKER_COLOR = '#fe0c00';
const SELECTED = 'selected';

// Following constants should be equal to `STATUS_CHOICES` in `RoomStatus` model
const AVAILABLE = 'available';
const OFFLINE_PERIOD = 'offline_period';
const RESERVED = 'reserved';
const BOOKED = 'booked';
const OCCUPIED = 'occupied';

// Following constants should be equal to the unique `BookingType`'s names
const LONG_STAY = 'Long Stay';
const EXTENDED_STAY = 'Extended Stay';
const GUEST = 'Guest';
const CORPORATE = 'Corporate';

// Booking status categories
// Important: BOOKED === OCCUPIED
const LONG_STAY_RESERVED = `${LONG_STAY} ${RESERVED}`;
const LONG_STAY_OCCUPIED = `${LONG_STAY} ${OCCUPIED}`;
const LONG_STAY_BOOKED = `${LONG_STAY} ${BOOKED}`;
const EXTENDED_STAY_RESERVED = `${EXTENDED_STAY} ${RESERVED}`;
const EXTENDED_STAY_OCCUPIED = `${EXTENDED_STAY} ${OCCUPIED}`;
const EXTENDED_STAY_BOOKED = `${EXTENDED_STAY} ${BOOKED}`;
const GUEST_RESERVED = `${GUEST} ${RESERVED}`;
const GUEST_OCCUPIED = `${GUEST} ${OCCUPIED}`;
const GUEST_BOOKED = `${GUEST} ${BOOKED}`;
const CORPORATE_RESERVED = `${CORPORATE} ${RESERVED}`;
const CORPORATE_OCCUPIED = `${CORPORATE} ${OCCUPIED}`;
const CORPORATE_BOOKED = `${CORPORATE} ${BOOKED}`;

const OUT_OF_SERVICE = 'Out of service';

// Custom styles per item type
const STYLES = {
  [AVAILABLE]: {
    backgroundColor: 'var(--white)',
    color: 'var(--ash-grey-two)',
    border: '0.1rem solid var(--dark-cloud)'
  },
  [OFFLINE_PERIOD]: {
    backgroundColor: 'var(--pale-grey-five)',
    color: 'var(--white)',
    border: '0.1rem solid #3b5775'
  },
  [LONG_STAY_RESERVED]: {
    background:
      'repeating-linear-gradient(-45deg, #49cd35, #49cd35 0.07rem, #a8e193 0, #a8e193 0.4rem )',
    color: '#317e17',
    border: '0.1rem solid #5fba3f'
  },
  [LONG_STAY_OCCUPIED]: {
    backgroundColor: '#5fba3f',
    color: 'var(--white)',
    border: '0.1rem solid #017500'
  },
  [LONG_STAY_BOOKED]: {
    backgroundColor: '#6fca4f',
    color: 'var(--white)',
    border: '0.1rem solid #017500'
  },
  [EXTENDED_STAY_RESERVED]: {
    backgroundColor: '#ffcb93',
    color: '#bd6200',
    border: '0.1rem solid #ff9626'
  },
  [EXTENDED_STAY_OCCUPIED]: {
    backgroundColor: '#ff9626',
    color: 'var(--white)',
    border: '0.1rem solid #ff2d00'
  },
  [EXTENDED_STAY_BOOKED]: {
    backgroundColor: '#ff9626',
    color: 'var(--white)',
    border: '0.1rem solid #ff2d00'
  },
  [CORPORATE_RESERVED]: {
    backgroundColor: '#cce1ff',
    color: '#426bbd',
    border: '0.1rem solid #71a9fc'
  },
  [CORPORATE_OCCUPIED]: {
    backgroundColor: '#71a9fc',
    color: 'var(--white)',
    border: '0.1rem solid #0053f9'
  },
  [CORPORATE_BOOKED]: {
    backgroundColor: '#71a9fc',
    color: 'var(--white)',
    border: '0.1rem solid #0053f9'
  },
  [GUEST_RESERVED]: {
    backgroundColor: '#ceb8f2',
    color: '#6b3ac2',
    border: '0.1rem solid #a17fd8'
  },
  [GUEST_OCCUPIED]: {
    backgroundColor: '#a17fd8',
    color: 'var(--white)',
    border: '0.1rem solid #431db1'
  },
  [GUEST_BOOKED]: {
    backgroundColor: '#a17fd8',
    color: 'var(--white)',
    border: '0.1rem solid #431db1'
  },
  [SELECTED]: {
    backgroundColor: '#ffc109',
    color: 'var(--white)',
    border: '0.1rem solid #ffc109'
  }
};

const getOfflinePeriodTitle = ({offlinePeriodReason, offlinePeriodNotes}) => {
  if (offlinePeriodNotes === '') {
    return `OOO (${offlinePeriodReason})`;
  }
  return `OOO (${offlinePeriodReason}, ${offlinePeriodNotes})`;
};

const TITLES = {
  [AVAILABLE]: () => 'Available',
  [OFFLINE_PERIOD]: getOfflinePeriodTitle,
  [LONG_STAY_RESERVED]: item =>
    `Long Stay | Reserved | for ${item.tenants.join(', ')}`,
  [LONG_STAY_OCCUPIED]: item =>
    `Long Stay | Occupied | for ${item.tenants.join(', ')}`,
  [LONG_STAY_BOOKED]: item =>
    `Long Stay | Booked | for ${item.tenants.join(', ')}`,
  [EXTENDED_STAY_RESERVED]: item =>
    `Extended Stay | Reserved | for ${item.tenants.join(', ')}`,
  [EXTENDED_STAY_OCCUPIED]: item =>
    `Extended Stay | Occupied | for ${item.tenants.join(', ')}`,
  [EXTENDED_STAY_BOOKED]: item =>
    `Extended Stay | Booked | for ${item.tenants.join(', ')}`,
  [GUEST_RESERVED]: item => `Guest | for ${item.tenants.join(', ')}`,
  [GUEST_OCCUPIED]: item => `Guest | Occupied | for ${item.tenants.join(', ')}`,
  [GUEST_BOOKED]: item => `Guest | Booked | for ${item.tenants.join(', ')}`,
  [CORPORATE_RESERVED]: item => `Corporate | for ${item.tenants.join(', ')}`,
  [CORPORATE_OCCUPIED]: item =>
    `Corporate | Occupied | for ${item.tenants.join(', ')}`,
  [CORPORATE_BOOKED]: item =>
    `Corporate | Booked | for ${item.tenants.join(', ')}`
};

class AllocationsCalendar extends React.Component<any> {
  state = {
    statisticsBoxOpen: false
  };
  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  onTimeChange = (visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
    /*
     * <Timeline> receives onTimeChange prop to limit the calendar scroll manually.
     *
     * The function implementation is copy-pasted from react-calendar-timeline documentation
     * https://github.com/namespace-ee/react-calendar-timeline#ontimechangevisibletimestart-visibletimeend-updatescrollcanvas
    */
    if (visibleTimeStart < TIMELINE_START && visibleTimeEnd > TIMELINE_END)
      return updateScrollCanvas(TIMELINE_START, TIMELINE_END);

    if (visibleTimeStart < TIMELINE_START)
      return updateScrollCanvas(
        TIMELINE_START,
        TIMELINE_START + (visibleTimeEnd - visibleTimeStart)
      );

    if (visibleTimeEnd > TIMELINE_END)
      return updateScrollCanvas(
        TIMELINE_END - (visibleTimeEnd - visibleTimeStart),
        TIMELINE_END
      );

    return updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
  };

  getItemStatus = item => {
    const {status, bookingType} = item;

    if (!_.isNil(bookingType)) {
      return `${bookingType} ${status}`;
    }

    return status;
  };

  getItemStyles = ({item, itemContext}) => {
    let customItemStyles = STYLES[this.getItemStatus(item)];

    if (itemContext.selected) {
      customItemStyles = STYLES[SELECTED];
    }

    return {
      ...customItemStyles,
      borderRadius: '0.3rem',
      textAlign: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      justifyContent: 'spaceBetween'
    };
  };

  getItemTitle = item => {
    const itemStatus = this.getItemStatus(item);

    return _.get(TITLES, itemStatus, () => `Unknown status ${itemStatus}`)(
      item
    );
  };

  itemRenderer = ({item, getItemProps, itemContext}) => (
    <div {...getItemProps({style: this.getItemStyles({item, itemContext})})}>
      {this.getItemTitle(item)}
    </div>
  );

  changeRoomCleaningStatus = (cleaningStatus, roomId) => {
    const {changeCleaningStatus, locationRef, requiresPermission} = this.props;

    requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CHANGE_ROOM_CLEANING_STATUS
      },
      () => {
        const extraActions = [
          () => fetchAllocations({locationRef}),
          () => fetchAllocationStatistics({locationRef})
        ];

        changeCleaningStatus(
          {
            cleaningStatus,
            roomId,
            locationRef
          },
          extraActions
        );
      }
    );
  };

  onRoomCleaningStatusChange = (cleaningStatus, roomId) => {
    const {openOutOfServiceReasonModal} = this.props;

    if (cleaningStatus != OUT_OF_SERVICE) {
      this.changeRoomCleaningStatus(cleaningStatus, roomId);
    } else {
      openOutOfServiceReasonModal({cleaningStatus, roomId});
    }
  };

  groupRenderer = ({group, isRightSidebar}) => {
    return (
      <div>
        {!isRightSidebar && (
          <TimelineGroup
            group={group}
            onRoomCleaningStatusChange={this.onRoomCleaningStatusChange}
            openRoomNotesModal={this.props.openRoomNotesModal}
          />
        )}
      </div>
    );
  };

  getTimelineStart = () => {
    const {filterStartDate} = this.props;

    if (!_.isNil(filterStartDate)) {
      // `isoWeek` sets the start of the week according to ISO 8601 (will be timezone independent)
      return moment(filterStartDate, DATE_FORMAT_API).startOf('isoWeek');
    }

    return moment().add(-1, 'day');
  };

  getTimelineEnd = () => {
    const {filterEndDate} = this.props;

    if (!_.isNil(filterEndDate)) {
      // `isoWeek` sets the end of the week according to ISO 8601 (will be timezone independent)
      return moment(filterEndDate, DATE_FORMAT_API).endOf('isoWeek');
    }

    return moment().add(29, 'day');
  };

  leftSidebarHeaderContent = (
    <div className="sidebarContent">
      <div className="room colab-bold">Room</div>
      <div className="status colab-bold">Housekeeping</div>
      <div className="type colab-bold">Type</div>
    </div>
  );

  render() {
    const timelineStart = this.getTimelineStart();
    const timelineEnd = this.getTimelineEnd();
    const {statisticsBoxInfo, statisticsBoxOpen, timeline} = this.props;

    return (
      <div className="AllocationsCalendar">
        <StatisticsBox
          show={statisticsBoxOpen}
          closeBox={this.props.closeStatisticsBox}
          data={statisticsBoxInfo}
        />
        <Timeline
          lineHeight={40}
          groups={timeline.groups}
          itemTouchSendsClick={false}
          items={this.props.timeline.items}
          keys={KEYS_MAPPING}
          sidebarWidth={250}
          sidebarContent={this.leftSidebarHeaderContent}
          canMove={false}
          canChangeGroup={false}
          defaultTimeStart={timelineStart}
          defaultTimeEnd={timelineEnd}
          onTimeChange={this.onTimeChange}
          onItemDoubleClick={this.props.doubleClickHandler}
          itemRenderer={this.itemRenderer}
          canResize={false}
          groupRenderer={this.groupRenderer}>
          <TodayMarker>
            {/* TodayMarker ref: https://github.com/namespace-ee/react-calendar-timeline#todaymarker */}
            {({styles}) => (
              <div style={{...styles, background: TODAY_MARKER_COLOR}} />
            )}
          </TodayMarker>
        </Timeline>
        <ReactTooltip
          place="right"
          delayShow={300}
          effect="solid"
          id={TOOLTIP_NAME}
        />
      </div>
    );
  }
}

const mapDispatchToProps = {
  requiresPermission
};

export default connect(null, mapDispatchToProps)(AllocationsCalendar);
